import { useEffect, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';

import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';

type Props = {
  filterDate: {
    from: Date | undefined,
    to: Date | undefined
  },
  handleFilterDateChange: (fromDate, toDate) => void
}

const LastConversationFilter = ({ filterDate, handleFilterDateChange }: Props) => {
  const [selected, setSelected] = useState<'from' | 'to'>('from');
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();

  useEffect(() => {
    if (filterDate?.from && filterDate?.to && !isSameDay(fromDate, new Date(filterDate?.from)) && !isSameDay(toDate, new Date(filterDate?.to))) {
      setFromDate(new Date(filterDate?.from));
      setToDate(new Date(filterDate?.to));
    }
  }, [filterDate]);

  useEffect(() => {
    handleFilterDateChange(fromDate, toDate)
  }, [fromDate, toDate])

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-center gap-4.5 w-full">
        <div className="flex flex-col gap-1 w-full">
          <label className="font-bold text-xs">From</label>
          <Button
            className={`relative bg-transparent px-3 pr-7 py-2 border rounded text-xs font-normal leading-4 hover:bg-transparent justify-start ${
              selected === 'from' ? 'border-secondary' : 'border-[#D7D7D7]'
            }`}
            onClick={() => setSelected('from')}
          >
            {fromDate ? format(fromDate, 'dd/MM/yyyy') : ''}
            <CalendarIcon className="absolute w-4 h-4 top-1/2 -translate-y-1/2 right-3" />
          </Button>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <label className="font-bold text-xs">To</label>
          <Button
            className={`relative bg-transparent px-3 pr-7 py-2 border rounded text-xs font-normal leading-4 hover:bg-transparent justify-start ${
              selected === 'to' ? 'border-secondary' : 'border-[#D7D7D7]'
            }`}
            onClick={() => setSelected('to')}
          >
            {toDate ? format(toDate, 'dd/MM/yyyy') : ''}
            <CalendarIcon className="absolute w-4 h-4 top-1/2 -translate-y-1/2 right-3" />
          </Button>
        </div>
      </div>
      {(fromDate && !toDate) || (!fromDate && toDate) ? (
        <div className='text-xxs text-negative-red font-normal mt-1 mx-auto'>
          Please enter from and to dates
        </div>
      ) : null}
      <div className="border border-[#D7D7D7] rounded mt-3 mb-1.5">
        <Calendar
          mode="single"
          defaultMonth={new Date()}
          selected={selected === 'from' ? fromDate : toDate}
          onSelect={selected === 'from' ? setFromDate : setToDate}
          fromDate={selected === 'to' ? fromDate : null}
          toDate={new Date()}
        />
      </div>
      <div className='ml-auto flex items-center gap-2'>
        <Button
          className='border-none bg-transparent hover:bg-transparent p-1 text-secondary text-xs font-medium h-fit'
          onClick={() => {
            setFromDate(undefined);
            setToDate(undefined);
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default LastConversationFilter;
